import { useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Scoreboard from '@/pages/detail/components/Scoreboard';
import StatsPanel from '@/pages/detail/components/StatsPanel';
import LineupPanel from '@/pages/detail/components/LineupPanel';
import RelatedMatches from '@/pages/detail/components/RelatedMatches';
import { getMatchById, leagueMeta, relatedMatches, setPageMeta } from '@/lib/site';

export default function DetailPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const match = matchId ? getMatchById(matchId) : undefined;

  useEffect(() => {
    if (!match) return;
    const league = leagueMeta[match.league];
    const season = match.round.split('常规赛')[0]?.trim() || league.name;
    setPageMeta(
      `【${season}】${match.home.name}VS${match.away.name}直播 - 篮球直播`,
      `${match.timeLabel}，${match.home.name}对阵${match.away.name}，${match.leagueLabel}${match.round}。查看双方阵容、实时赛况演示与直播状态，篮球直播同步追踪本场赛事。`,
      `${match.home.name}vs${match.away.name},${match.leagueLabel}直播,篮球直播,篮球比赛`,
    );
  }, [match]);

  if (!match) return <Navigate to="/" replace />;

  const league = leagueMeta[match.league];
  const related = relatedMatches(match, 4);

  return (
    <SiteLayout>
      <div className="w-full bg-background-50 border-b border-background-200">
        <div className="w-full px-4 md:px-6 lg:px-8 py-4">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: `${league.name}赛事直播`, to: `/category/${match.league}` },
              { label: `${match.home.name} vs ${match.away.name}` },
            ]}
          />
          <div className="mt-3 flex flex-col md:flex-row md:items-end justify-between gap-3">
            <div>
              <h1 className="font-heading text-xl md:text-2xl text-foreground-950 leading-snug">
                【{match.round.split('常规赛')[0]?.trim() || league.name}】{match.home.name}VS{match.away.name}直播
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-600">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {match.timeLabel}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-trophy-line text-primary-600" />
                  {match.leagueLabel} · {match.round}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-map-pin-line" />
                  {match.venue}
                </span>
              </div>
            </div>
            <Link
              to={`/category/${match.league}`}
              className="inline-flex items-center gap-1.5 self-start rounded-md border border-background-300 bg-background-100 px-3 py-2 text-xs text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              返回 {match.leagueLabel} 赛事列表
            </Link>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 lg:px-8 py-5 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
          <div className="lg:col-span-8 space-y-4 md:space-y-5 min-w-0">
            <Scoreboard match={match} />
            <StatsPanel match={match} />

            <section aria-labelledby="lineup-title" className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 id="lineup-title" className="font-heading text-base text-foreground-950 flex items-center gap-2">
                  <i className="ri-team-line text-primary-600" />
                  双方阵容
                </h2>
                <span className="text-[11px] text-foreground-500">名单为示例信息，以官方公布为准</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                <LineupPanel match={match} team={match.home} side="home" />
                <LineupPanel match={match} team={match.away} side="away" />
              </div>
            </section>

            <section aria-labelledby="summary-title" className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
              <h2 id="summary-title" className="font-heading text-base text-foreground-950 flex items-center gap-2">
                <i className="ri-article-line text-primary-600" />
                赛前前瞻与看点
              </h2>
              <p className="mt-2.5 text-sm leading-relaxed text-foreground-700">{match.summary}</p>
              <ul className="mt-3 space-y-2">
                {match.highlights.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-xs text-foreground-600">
                    <i className="ri-checkbox-blank-circle-fill text-[6px] text-accent-500 mt-1.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-4 min-w-0">
            <div className="rounded-lg bg-background-50 border border-background-200 p-4">
              <h3 className="font-heading text-sm tracking-wider text-foreground-900 mb-3">赛事信息</h3>
              <dl className="space-y-2.5 text-xs">
                <InfoRow label="所属联赛" value={`${league.name}（${match.leagueLabel}）`} />
                <InfoRow label="赛事轮次" value={match.round} />
                <InfoRow label="开赛时间" value={match.timeLabel} />
                <InfoRow label="比赛场馆" value={match.venue} />
                <InfoRow label="直播信号" value={match.broadcaster} />
                <InfoRow label="观看热度" value={match.views} />
              </dl>
              <Link
                to={`/category/${match.league}`}
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary-900 px-4 py-2.5 text-xs text-background-50 hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                查看 {match.leagueLabel} 全部赛事
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <RelatedMatches items={related} leagueLabel={league.name} leaguePath={`/category/${match.league}`} />
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-foreground-500 whitespace-nowrap">{label}</dt>
      <dd className="text-foreground-900 text-right leading-relaxed">{value}</dd>
    </div>
  );
}